import React, { useContext, useEffect, useRef, useState } from 'react';
import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";
import { Alert, Button, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import DataLoadingError from "@components/common/DataLoadingError";
import useErrorHandling from '@components/common/hooks/useErrorHandling';
import useEventDemographics from '@components/common/hooks/api/useEventDemographics';
import useEventDetailsQuery from "@components/common/hooks/api/useEventDetailsQuery";
import EventDemographicsForm from '@components/ContentComponents/Events/Demographics/EventDemographicsForm';
import { axiosRequest } from "@lib/axios";
import { PageContext } from '@lib/contexts/pageContext';
import UserContext from '@lib/contexts/userContext';
import moment from '@lib/moment';
import { AbilityContext, Subjects } from "@lib/permissions";

const CompleteEvent = () => {
  const queryClient = useQueryClient();

  const { setBreadcrumbValue, setPageErrorMessage } = useContext(PageContext);
  const { userRecord } = useContext(UserContext);
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);

  const { eventID } = useParams();

  const [submitting, setSubmitting] = useState(false);
  const [wasSubmitting, setWasSubmitting] = useState(false);

  const submitRef = useRef();

  const form = useForm({
    mode: 'onSubmit',
  })

  const {
    formState: { errors, isSubmitting },
  } = form;

  useErrorHandling({
    errors,
    isSubmitting,
    wasSubmitting,
    setWasSubmitting,
    // modalContext,
    scrolling: false,
  });

  const { data: event, ...eventQuery } = useEventDetailsQuery(eventID);

  /**
   * Mutation for marking events as "Complete"
   */
  const completeMutation = useMutation({
    mutationFn: async (data) => axiosRequest({
      method: 'PUT',
      url: `/events/${eventID}`,
      data,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate(`/events/${eventID}`);
    },
    onError: (err) => {
      setSubmitting(false);
      setPageErrorMessage('Error completing event', err);
      console.log('[SessionDetails][complete] error:', err);
    },
  });

  useEffect(() => {
    if (submitting) {
      return;
    }
    if (event) {
      setBreadcrumbValue(':event', event);
    }
  }, [event, setBreadcrumbValue, submitting]);

  const { saveDemographics } = useEventDemographics(event);

  /**
   * Handles the successful validation of the demographics form submission.
   */
  const onSubmit = async (values) => {
    setSubmitting(true);

    try {
      const result = await saveDemographics(event, values);
      if (!result) {
        setSubmitting(false);
        return false;
      }

      const input = {
        status: 'COMPLETED',
        comments: values?.comments || '',
        completed_by: userRecord.email,
        completedAt: moment().toISOString(true),
      };
      await completeMutation.mutateAsync(input);
    } catch (error) {
      setPageErrorMessage(error);
      setSubmitting(false);
      return false;
    }
  };

  if (eventQuery.isLoading) {
    return (
      <Box width="100%" textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (eventQuery.isError && eventQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load event!'} errors={eventQuery.error.errors} />;
  }

  if (!event) {
    return (
      <Box width="100%" textAlign="center">
        <Alert severity="error">You do not have access, or the event was not found.</Alert>
      </Box>
    );
  }

  if (ability.cannot('edit', subject(Subjects.EVENT, event))) {
    return (
      <Box width="100%" textAlign="center">
        <Alert severity="error">You do not have permission to edit this event.</Alert>
      </Box>
    );
  }

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        <EventDemographicsForm
          event={event}
          onSubmit={onSubmit}
          submitRef={submitRef}
          showParticipants
          validateDemographics
        />
        <Button
          id="button-submit"
          disabled={submitting}
          variant="contained"
          size="small"
          onClick={() => {
            submitRef.current.click();
          }}
          sx={{ marginTop: '20px' }}
        >
          Save & Complete Event
        </Button>
      </Grid>
    </Grid>
  );
};

export default CompleteEvent;
